import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import { useSignupIntent } from '../../hooks/use-signup-intent';
import { loadOwnBusiness } from '../../lib/business/own-business';
import { resolveEntryDestination, type Presenca } from '../../lib/auth/entry-destination';
import { Aguardar } from '../../lib/auth/session-guard';
import { hasConsumerProfile } from '../../lib/onboarding/consumer-profile';

/**
 * Entrada pós-login (T1.19b, gate D3 — ADR 0039): leva a conta nova ao onboarding que escolheu.
 *
 * Monta só no `/` autenticado, dentro do `LegalReacceptanceGate` — um re-aceite pendente vence o
 * redirecionamento. Não envolve as rotas de destino, logo não há ciclo possível.
 *
 * 🔴 O negócio é lido no **banco**, não no claim `business_id`: quem acabou de criar o rascunho tem
 * um token com o claim ainda nulo por até 900 s, e decidir pelo claim reabriria o risco de dois
 * negócios por dono (T1.19).
 */
export function EntryRedirect({ children }: { children: ReactNode }) {
  const { role, isLoading } = useAuth();
  const signupIntent = useSignupIntent();
  const [presencas, setPresencas] = useState<{ business: Presenca; consumerProfile: Presenca } | null>(null);
  const precisaLer = !isLoading && role === 'consumer';

  useEffect(() => {
    if (!precisaLer) return;
    let vivo = true;

    void Promise.all([loadOwnBusiness(), hasConsumerProfile()]).then(([negocio, perfil]) => {
      if (!vivo) return;
      setPresencas({
        business: !negocio.ok ? 'unknown' : negocio.data ? 'exists' : 'none',
        consumerProfile: !perfil.ok ? 'unknown' : perfil.data ? 'exists' : 'none',
      });
    });

    return () => {
      vivo = false;
    };
  }, [precisaLer]);

  if (isLoading) return <Aguardar />;
  if (role !== 'consumer') return <>{children}</>;
  if (!presencas) return <Aguardar />;

  const destino = resolveEntryDestination({ role, signupIntent, ...presencas });
  return destino ? <Navigate to={destino} replace /> : <>{children}</>;
}
